import type { Config } from 'payload'
import { en } from '@payloadcms/translations/languages/en'
import { pl } from '@payloadcms/translations/languages/pl'
import { wlodevAuthStrategy } from './auth/auth.js'
import { uploadPlugin } from './upload/index.js'
import { PluginOptions } from './types.js'

type WlodevPlugin = (
    pluginOptions?: PluginOptions
) => (config: Config) => Config
export const wlodevPlugin: WlodevPlugin =
    pluginOptions =>
    (incomingConfig: Config): Config => {
        const config = { ...incomingConfig }

        return uploadPlugin()({
            ...config,
            /**
             * Payloadcms authentication is disabled in the production environment as this is
             * handled entirely by the wlodev cloud and wlodev services. To avoid confusion
             * we just hide components and collections related to auth in localhost and disable it.
             */
            i18n: {
                ...config.i18n,
                ...pluginOptions?.i18n,
                fallbackLanguage:
                    config.i18n?.fallbackLanguage ??
                    pluginOptions?.i18n?.fallbackLanguage ??
                    'en',
                supportedLanguages: {
                    en,
                    pl,
                    ...config.i18n?.supportedLanguages,
                    ...pluginOptions?.i18n?.supportedLanguages,
                },
            },
            admin: {
                ...config.admin,
                avatar: {
                    Component: '@wlodev/payload/client#Avatar',
                },
                components: {
                    ...config.admin.components,
                    logout: {
                        Button: '@wlodev/payload/client#LogoutButton',
                    },
                },
            },
            collections: (config.collections || []).map(collection => {
                /**
                 * Auth is handled by the wlodev services and wlodev cloud, thus
                 * we disable payloadcms auth in the production environment.
                 */
                if (collection.auth || collection.slug === 'users') {
                    collection = {
                        ...collection,
                        admin: {
                            ...collection.admin,
                            hidden: true,
                        },
                        auth: {
                            disableLocalStrategy: true,
                            strategies: [wlodevAuthStrategy(collection)],
                        },
                    }
                } else {
                    // We need to disable access control for reads as build process does not handle authentication by default.
                    // TODO: Authenticate build server to access payload data
                    collection = {
                        ...collection,
                        access: {
                            read: () => true,
                            ...collection.access,
                        },
                    }
                }

                return collection
            }),
        })
    }

export { getSiteURL } from './utils.js'
